import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { convertNumberToHMS } from '../Admin/globalFunctions';

interface PopoverItem {
    id: number;
    name: string;
    count: number;
    avgDuration: number;
}

interface SearchablePopoverProps {
    items: PopoverItem[];
    label: string;
    searchPlaceholder: string;
    activeId: number | null;
    onSelect: (item: PopoverItem) => void;
    showRank?: boolean;
    hideAvg?: boolean;
    style?: React.CSSProperties;
    popoverRef?: React.Ref<HTMLDivElement>;
}

const SearchablePopover: React.FC<SearchablePopoverProps> = ({
    items, label, searchPlaceholder, activeId, onSelect, showRank, hideAvg, style, popoverRef
}) => {
    const [search, setSearch] = useState('');

    const filtered = search
        ? items.filter(item => (item.name || '').toLowerCase().includes(search.toLowerCase()))
        : items;

    return (
        <div ref={popoverRef} style={{
            position: 'absolute', zIndex: 50, background: '#fff', borderRadius: 10,
            boxShadow: '0 8px 32px rgba(0,0,0,0.13)', border: '1px solid #e2e8f0',
            padding: '6px 0', minWidth: 300, maxHeight: 400, display: 'flex', flexDirection: 'column',
            ...style
        }}>
            {/* Search */}
            <div style={{ padding: '6px 12px 4px', background: '#fff' }}>
                <div style={{ position: 'relative' }}>
                    <svg style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder={searchPlaceholder}
                        autoFocus
                        style={{ width: '100%', padding: '6px 8px 6px 28px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, outline: 'none', fontFamily: 'inherit' }}
                        onClick={e => e.stopPropagation()}
                    />
                </div>
            </div>

            {/* Column headers */}
            <div style={{ padding: '4px 16px 4px', fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                <span>{label}</span>
                <span style={{ display: 'flex', gap: 10 }}>{!hideAvg && <span><FormattedMessage id="production.popover.avgHeader" defaultMessage="Mesatare" /></span>}<span><FormattedMessage id="production.popover.totalHeader" defaultMessage="Total" /></span></span>
            </div>

            {/* List */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
                {filtered.length === 0 ? (
                    <div style={{ padding: '12px 16px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}><FormattedMessage id="production.popover.noResults" defaultMessage="Asnjë rezultat" /></div>
                ) : (
                    filtered.map((item, i) => (
                        <div
                            key={item.id}
                            style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '7px 16px', fontSize: 13, color: '#334155', cursor: 'pointer',
                                transition: 'background 0.1s', background: activeId === item.id ? '#dbeafe' : 'transparent'
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = activeId === item.id ? '#dbeafe' : '#f1f5f9')}
                            onMouseLeave={e => (e.currentTarget.style.background = activeId === item.id ? '#dbeafe' : 'transparent')}
                            onClick={() => onSelect(item)}
                        >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                {showRank && (
                                    <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#1e3a5f', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                                )}
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, marginLeft: 8 }}>
                                {!hideAvg && <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>{convertNumberToHMS(item.avgDuration)}</span>}
                                <span style={{ fontWeight: 600, color: '#1e3a5f', minWidth: 24, textAlign: 'right' }}>{item.count}</span>
                            </span>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default SearchablePopover;
